import { useEffect, useState } from 'react';

import { useGlobalState } from '@/hooks';
import type {
  ICarrier,
  ICarrierWithFirstFoundEvent,
  IParcelEvent,
  ParcelEventAnalyticsType,
} from '@/modules/pmt/interfaces';
import { trackingEventDateModify } from '@/modules/pmt/utils';

export interface IModifiedEvent {
  id: number;
  date: string;
  time: string;
  name: string;
  location?: string | null;
  analytics?: ParcelEventAnalyticsType;
  carrier?: ICarrier;
}

interface IEventGroup {
  date: string; // e.g. Today, 10 Sep 22
  eventList: IModifiedEvent[];
}

type UseTrackingEventList = (args: {
  carrierList: ICarrierWithFirstFoundEvent[];
  eventList: IParcelEvent[];
}) => IEventGroup[];

const modifyEventList = (eventList: IParcelEvent[], carrierList: ICarrierWithFirstFoundEvent[]) => {
  return (t: (key: string) => string) => {
    return eventList.map(({ id, date, type, key, location, analytics, carrier }) => {
      const modifiedEvent: IModifiedEvent = {
        id,
        date: trackingEventDateModify(
          date,
          {
            showDate: true,
            showTime: false,
            fullDay: true,
            showDay: true,
          },
          t,
        ) as string,
        time: trackingEventDateModify(
          date,
          {
            showDate: false,
            showTime: true,
            fullDay: false,
            showDay: true,
          },
          t,
        ) as string,
        name: key ? t(key) : type,
        location,
        analytics,
      };

      const hasCarrierInfo = carrierList.some((item) => item.firstFoundInEvent.id === id);

      if (hasCarrierInfo) {
        modifiedEvent.carrier = carrier;
      }

      return modifiedEvent;
    });
  };
};

const groupEventList = (eventList: IModifiedEvent[]) => {
  return eventList.reduce<IEventGroup[]>((currentGroupList, event) => {
    let foundGroup = currentGroupList.find((group) => group.date === event.date);

    if (!foundGroup) {
      foundGroup = {
        date: event.date,
        eventList: [],
      };
      currentGroupList.push(foundGroup);
    }

    foundGroup.eventList.push(event);

    return currentGroupList;
  }, []);
};

export const useTrackingEventList: UseTrackingEventList = ({ carrierList, eventList }) => {
  const { t } = useGlobalState();

  const [groupedEventList, setGroupedEventList] = useState<IEventGroup[]>([]);

  useEffect(() => {
    const modifiedEvents = modifyEventList(eventList, carrierList)(t);
    const groupedEvents = groupEventList(modifiedEvents);

    setGroupedEventList(groupedEvents);
  }, [eventList, carrierList, t]);

  return groupedEventList;
};
